import { getFinalHairClass } from './chatAnswerHelpers'

const getLang = (language) => {
  const normalized = String(language || 'EN')
    .trim()
    .toUpperCase()

  return ['EN', 'FR', 'NL', 'SW'].includes(normalized)
    ? normalized
    : 'EN'
}

const translateProfileLabel = ({
  translations,
  language,
  key,
  fallback,
}) => {
  const lang = getLang(language)

  const entry =
  translations?.menu?.profileFields?.[key]

  return (
    entry?.[lang] ||
    entry?.EN ||
    fallback
  )
}

const translateProfileValue = ({
  translations,
  language,
  value,
}) => {
  if (value == null || value === '') {
    return '—'
  }

  const lang = getLang(language)

  const translateSingle = (item) => {
    const stringValue = String(item).trim()

    const translated =
  translations?.menu?.profileValues?.[stringValue]

    return (
      translated?.[lang] ||
      translated?.EN ||
      stringValue
    )
  }

  if (Array.isArray(value)) {
    return value.length
      ? value.map(translateSingle).join(', ')
      : '—'
  }

  if (typeof value === 'object') {
    return '—'
  }

  return translateSingle(value)
}

const translateCanonicalProfileValue = ({
  translations,
  language,
  canonicalValue,
  fallbackValue,
}) => {
  if (
    canonicalValue == null ||
    canonicalValue === ''
  ) {
    return translateProfileValue({
      translations,
      language,
      value: fallbackValue,
    })
  }

  const lang = getLang(language)

  const translateSingle = (
    canonicalId,
    fallback = null,
  ) => {
    const entry =
      translations?.menu?.questionnaireValues?.[
        String(canonicalId).trim()
      ]

    return (
      entry?.[lang] ||
      entry?.EN ||
      fallback ||
      String(canonicalId)
    )
  }

  if (Array.isArray(canonicalValue)) {
    if (!canonicalValue.length) {
      return '—'
    }

    const fallbackList = Array.isArray(fallbackValue)
      ? fallbackValue
      : []

    return canonicalValue
      .map((canonicalId, index) =>
        translateSingle(
          canonicalId,
          fallbackList[index],
        ),
      )
      .join(', ')
  }

  return translateSingle(
    canonicalValue,
    Array.isArray(fallbackValue)
      ? fallbackValue[0]
      : fallbackValue,
  )
}

export const formatProfileData = (
  answersMap,
  translations = {},
  language = 'EN',
) => {
  const item = (
    key,
    fallbackLabel,
    value,
  ) => ({
    label: translateProfileLabel({
      translations,
      language,
      key,
      fallback: fallbackLabel,
    }),

    value: translateProfileValue({
      translations,
      language,
      value,
    }),
  })

  const canonicalItem = (
    key,
    fallbackLabel,
    canonicalValue,
    fallbackValue,
  ) => ({
    label: translateProfileLabel({
      translations,
      language,
      key,
      fallback: fallbackLabel,
    }),

    value: translateCanonicalProfileValue({
      translations,
      language,
      canonicalValue,
      fallbackValue,
    }),
  })

  return [
    item(
      'storeOwner',
      'Store Owner',
      answersMap.storeOwner,
    ),

    item(
      'referralSource',
      'Referral Source',
      answersMap.referralSource,
    ),

    item(
      'shoppingFor',
      'Shopping For',
      answersMap.shoppingFor,
    ),

    item(
      'age',
      'Age',
      answersMap.demographics?.age ||
        answersMap.age,
    ),

    item(
      'gender',
      'Gender',
      answersMap.demographics?.gender ||
        answersMap.gender,
    ),

    item(
      'location',
      'Location',
      answersMap.demographics?.location ||
        answersMap.location ||
        'City',
    ),

    item(
      'hairType',
      'Hair Type',
      getFinalHairClass(answersMap),
    ),

    canonicalItem(
      'hairState',
      'Hair State',
      answersMap.hairProfileCanonical?.hair_state,
      answersMap.hairProfile?.hair_state ||
        answersMap.hair_state ||
        'Natural',
    ),

    canonicalItem(
      'scalpChallenges',
      'Scalp Challenges',
      answersMap.hairProfileCanonical
        ?.scalp_challenges,
      answersMap.hairProfile?.scalp_challenges ||
        answersMap.scalp_challenges ||
        'None',
    ),

    canonicalItem(
      'hairChallenges',
      'Hair Challenges',
      answersMap.hairProfileCanonical
        ?.hair_challenges,
      answersMap.hairProfile?.hair_challenges ||
        answersMap.hair_challenges ||
        'None',
    ),

    canonicalItem(
      'hairGoals',
      'Hair Goals',
      answersMap.hairProfileCanonical?.hair_goals,
      answersMap.hairProfile?.hair_goals ||
        answersMap.hair_goals ||
        'None',
    ),

    item(
      'currentProducts',
      'Current Products',
      answersMap.routinePreferences?.current_products ||
        answersMap.current_products ||
        'None',
    ),

    item(
      'budget',
      'Budget',
      answersMap.routinePreferences?.budget_constraints ||
        answersMap.budget_constraints ||
        'None',
    ),

    item(
      'ecoPreference',
      'Eco Preference',
      answersMap.routinePreferences?.eco_preferences ||
        answersMap.eco_preferences ||
        'No',
    ),

    item(
      'routineFrequency',
      'Routine Frequency',
      answersMap.routinePreferences?.routine_frequency ||
        answersMap.routine_frequency ||
        'None',
    ),

    item(
      'allergiesSensitivities',
      'Allergies / Sensitivities',
      answersMap.medicalLifestyle?.allergies_or_sensitivities ||
        answersMap.allergies_or_sensitivities ||
        'No',
    ),

    item(
      'medicalConditions',
      'Medical Conditions',
      answersMap.medicalLifestyle?.medical_conditions ||
        answersMap.medical_conditions ||
        'No',
    ),

    item(
      'lifestyleFactors',
      'Lifestyle Factors',
      answersMap.medicalLifestyle?.lifestyle_factors ||
        answersMap.lifestyle_factors ||
        'None',
    ),

    item(
      'medicationsTreatments',
      'Medications or Treatments',
      answersMap.medicalLifestyle?.medications_or_treatments ||
        answersMap.medications_or_treatments ||
        'None',
    ),

    item(
      'pregnancyPostpartum',
      'Pregnancy / Postpartum',
      answersMap.medicalLifestyle?.pregnancy_or_postpartum ||
        answersMap.pregnancy_or_postpartum ||
        'No',
    ),
  ].filter(
    (profileItem) =>
      profileItem.value !== '—',
  )
}

export const formatReturningProfileData = (
  agentData = {},
  translations = {},
  language = 'EN',
) => {
  const item = (
    key,
    fallbackLabel,
    value,
  ) => ({
    label: translateProfileLabel({
      translations,
      language,
      key,
      fallback: fallbackLabel,
    }),

    value: translateProfileValue({
      translations,
      language,
      value,
    }),
  })

  const canonicalItem = (
    key,
    fallbackLabel,
    value,
  ) => {
    const values = Array.isArray(value)
      ? value
      : value != null && value !== ''
        ? [value]
        : []

    const isCanonical =
      values.length > 0 &&
      values.every((entry) =>
        /^q\d+:A\d+$/i.test(
          String(entry).trim(),
        ),
      )

    return {
      label: translateProfileLabel({
        translations,
        language,
        key,
        fallback: fallbackLabel,
      }),

      value: isCanonical
        ? translateCanonicalProfileValue({
            translations,
            language,
            canonicalValue: value,
            fallbackValue: value,
          })
        : translateProfileValue({
            translations,
            language,
            value,
          }),
    }
  }

  return [
    item(
      'age',
      'Age',
      agentData?.age_category,
    ),

    item(
      'gender',
      'Gender',
      agentData?.gender,
    ),

    item(
      'location',
      'Location',
      agentData?.place_name,
    ),

    canonicalItem(
      'hairState',
      'Hair State',
      agentData?.hair_state,
    ),

    canonicalItem(
      'hairGoals',
      'Hair Goals',
      agentData?.hair_goals,
    ),

    canonicalItem(
      'hairChallenges',
      'Hair Challenges',
      agentData?.hair_challenges,
    ),

    canonicalItem(
      'scalpChallenges',
      'Scalp Challenges',
      agentData?.scalp_challenges,
    ),

    item(
      'allergiesSensitivities',
      'Allergy / Medical',
      agentData?.allergy_or_medical,
    ),

    item(
      'pregnancyPostpartum',
      'Pregnancy / Postpartum',
      agentData?.pregnancy_or_postpartum,
    ),

    {
      label: translateProfileLabel({
        translations,
        language,
        key: 'lastDiagnosis',
        fallback: 'Last Diagnosis',
      }),
      value:
        agentData?.last_diagnostic ||
        'No previous diagnosis recorded',
    },

    {
      label: translateProfileLabel({
        translations,
        language,
        key: 'lastRoutine',
        fallback: 'Last Routine',
      }),
      value:
        agentData?.last_routine ||
        'No previous routine recorded',
    },

    {
      label: translateProfileLabel({
        translations,
        language,
        key: 'lastRecommendedProduct',
        fallback: 'Last Recommended Product',
      }),
      value:
        agentData?.product_name ||
        'No previous product recorded',
    },

    {
      label: translateProfileLabel({
        translations,
        language,
        key: 'productBrand',
        fallback: 'Product Brand',
      }),
      value:
        agentData?.product_brand ||
        'Not recorded',
    },

    {
      label: translateProfileLabel({
        translations,
        language,
        key: 'productCategory',
        fallback: 'Product Category',
      }),
      value:
        agentData?.product_category ||
        'Not recorded',
    },

    {
      label: translateProfileLabel({
        translations,
        language,
        key: 'productQuantity',
        fallback: 'Product Quantity',
      }),
      value:
        agentData?.product_quantity ||
        'Not recorded',
    },

    {
      label: translateProfileLabel({
        translations,
        language,
        key: 'productPrice',
        fallback: 'Product Price',
      }),
      value:
        agentData?.product_price != null
          ? `€${agentData.product_price}`
          : 'Not recorded',
    },

    {
      label: translateProfileLabel({
        translations,
        language,
        key: 'lastUpdated',
        fallback: 'Last Updated',
      }),
      value:
        agentData?.last_operation_timestamp ||
        'Not recorded',
    },
  ]
}